import * as React from "react";
const initialState = {
  status: "uninit",
  cameraError: null
};
function createRandomId(prefix) {
  return `${prefix}${Math.random().toString(16).substring(2)}`;
}
function loadQrcodeModule() {
  return import("html5-qrcode");
}
const useCodeScannerCamera = ({
  onCodeDetected,
  onError,
  cameraContainerRef,
  formatsToSupport
}) => {
  const scannerRef = React.useRef(null);
  const cameraContainerId = React.useMemo(() => createRandomId("scanner-"), []);
  const [{ cameraError, status }, setState] = useStateReducer({
    ...initialState
  });
  const stopScanner = React.useCallback(async () => {
    const { Html5QrcodeScannerState } = await loadQrcodeModule();
    const scanningState = scannerRef.current?.getState();
    if (scanningState === Html5QrcodeScannerState.SCANNING) {
      try {
        await scannerRef.current?.stop();
      } catch (e) {
        console.error(e);
      }
    }
    setState({ status: "stopped" });
  }, [setState]);
  const onScanSuccess = React.useCallback(
    (decodedText) => {
      onCodeDetected(decodedText);
      stopScanner();
    },
    [onCodeDetected, stopScanner]
  );
  const handleScannerError = React.useCallback(
    (err) => {
      console.debug("Code Scanner Error:", err);
      setState({
        status: "error",
        cameraError: err.toLowerCase().includes("permission denied") ? "permissionDenied" : "other"
      });
      onError && onError(err);
    },
    [onError, setState]
  );
  const getAspectRatio = React.useCallback((container) => {
    const height = container.offsetHeight;
    const width = container.offsetWidth;
    return width / height;
  }, []);
  const getQrBoxSize = React.useCallback(() => {
    const height = 336;
    let width = 288;
    const containerWidth = cameraContainerRef.current?.offsetWidth ?? 0;
    containerWidth > 768 ? width = 488 : width;
    return { height, width };
  }, [cameraContainerRef]);
  const initScanner = React.useCallback(async () => {
    const { Html5Qrcode } = await loadQrcodeModule();
    setState({ status: "loading" });
    let cameras = [];
    try {
      cameras = await Html5Qrcode.getCameras();
    } catch (e) {
      handleScannerError(typeof e === "string" ? e : "other");
    }
    if (!cameras?.length) return;
    cameraContainerRef?.current?.setAttribute("id", cameraContainerId);
    scannerRef.current = new Html5Qrcode(cameraContainerId, {
      verbose: false,
      ...!!formatsToSupport?.length ? { formatsToSupport } : {}
    });
    setState({ status: "ready" });
  }, [setState, handleScannerError, cameraContainerRef, cameraContainerId, formatsToSupport]);
  const startScanning = React.useCallback(async () => {
    if (!scannerRef?.current || status !== "ready" || !cameraContainerRef.current) {
      setState({ status: "error", cameraError: "other" });
      return;
    }
    const aspectRatio = getAspectRatio(cameraContainerRef.current);
    const qrbox = getQrBoxSize();
    try {
      await scannerRef.current.start(
        { facingMode: "environment" },
        { aspectRatio, fps: 10, qrbox },
        onScanSuccess,
        void 0
        // Do nothing for unsuccessful scans
      );
    } catch (e) {
      handleScannerError(typeof e === "string" ? e : "other");
    }
    setState({ status: "scanning" });
  }, [
    status,
    cameraContainerRef,
    getAspectRatio,
    getQrBoxSize,
    onScanSuccess,
    handleScannerError,
    setState
  ]);
  React.useEffect(() => {
    if (status === "ready") {
      startScanning();
    }
  }, [startScanning, status]);
  return {
    initScanner,
    stopScanner,
    status,
    cameraError
  };
};
function useStateReducer(initialState2) {
  return React.useReducer((state, action) => ({ ...state, ...action }), initialState2);
}
export {
  useCodeScannerCamera
};
//# sourceMappingURL=hooks.js.map
